import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import './OrderDetails.css';

const BulkOrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editedOrder, setEditedOrder] = useState(null);
  const [editedProducts, setEditedProducts] = useState([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchOrderDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`https://gokulnair.com/rpo_bulk/api/order-details/${orderId}`);
      
      // The API returns both order and products in one response
      if (response.data.order && response.data.products) {
        setOrder(response.data.order);
        setProducts(response.data.products);
        setEditedOrder(response.data.order);
        setEditedProducts(response.data.products);
      } else {
        setOrder(response.data);
        setEditedOrder(response.data);
        setEditedProducts([]);
      }
    } catch (err) {
      setError('Failed to fetch order details');
      console.error('Error fetching order details:', err);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const fetchAllProducts = useCallback(async () => {
    try {
      const response = await axios.get('https://gokulnair.com/rpo_bulk/api/products');
      setAllProducts(response.data);
    } catch (err) {
      console.error('Error fetching all products:', err);
    }
  }, []);

  useEffect(() => {
    fetchOrderDetails();
    fetchAllProducts();
  }, [fetchOrderDetails, fetchAllProducts]);



  const getProductInfo = (sku) => {
    if (!products || products.length === 0 || !sku) return null;
    
    // The API now returns products with quantities, so we can use them directly
    // But we still need to handle the case where we might have the old format
    if (products[0] && products[0].qty) {
      // New format: products already have quantities
      return products;
    } else {
      // Fallback: Handle multiple SKUs separated by commas (old format)
      const skuList = sku.split(',').map(s => s.trim());
      const productInfos = skuList.map(singleSku => {
        return products.find(product => product.sku === singleSku);
      }).filter(Boolean);
      
      return productInfos;
    }
  };

  const getPaymentTypeText = (type) => {
    switch (type) {
      case 0: return 'Zelle';
      case 1: return 'Cash App';
      case 2: return 'Stripe';
      default: return 'Unknown';
    }
  };

  const getPaymentStatusText = (status) => {
    switch (status) {
      case 0: return 'Pending';
      case 1: return 'Confirmed by User';
      case 2: return 'Confirmed by Admin';
      case 3: return 'Rejected by Admin';
      case 4: return 'Rejected by User';
      default: return 'Unknown';
    }
  };

  const getPaymentStatusClass = (status) => {
    switch (status) {
      case 0: return 'status-pending';
      case 1: return 'status-confirmed-user';
      case 2: return 'status-confirmed-admin';
      case 3: return 'status-rejected-admin';
      case 4: return 'status-rejected-user';
      default: return 'status-pending';
    }
  };

  const calculateTotal = (orderData = order) => {
    if (!orderData || !products) return 0;
    
    let total = 0;
    
    // Check if products have quantities (new API format)
    if (products[0] && products[0].qty) {
      // New format: products already have quantities
      products.forEach(product => {
        const quantity = parseInt(product.qty) || 0;
        total += parseFloat(product.price) * quantity;
      });
    } else {
      // Fallback: Handle multiple SKUs separated by commas (old format)
      if (!orderData.sku || !orderData.qty) return 0;
      
      const skuList = orderData.sku.split(',').map(s => s.trim());
      const qtyList = orderData.qty.split(',').map(q => q.trim());
      
      skuList.forEach((sku, index) => {
        const product = products.find(p => p.sku === sku);
        const quantity = parseInt(qtyList[index]) || 0;
        if (product) {
          total += parseFloat(product.price) * quantity;
        }
      });
    }
    
    const shippingCost = parseFloat(orderData.delivery_price) || 0;
    return total + shippingCost;
  };

  const calculateTotalWithCoupon = (orderData = order) => {
    if (!orderData || !products) return 0;
    
    let subtotal = 0;
    
    // Check if products have quantities (new API format)
    if (products[0] && products[0].qty) {
      // New format: products already have quantities
      products.forEach(product => {
        const quantity = parseInt(product.qty) || 0;
        subtotal += parseFloat(product.price) * quantity;
      });
    } else {
      // Fallback: Handle multiple SKUs separated by commas (old format)
      if (!orderData.sku || !orderData.qty) return 0;
      
      const skuList = orderData.sku.split(',').map(s => s.trim());
      const qtyList = orderData.qty.split(',').map(q => q.trim());
      
      skuList.forEach((sku, index) => {
        const product = products.find(p => p.sku === sku);
        const quantity = parseInt(qtyList[index]) || 0;
        if (product) {
          subtotal += parseFloat(product.price) * quantity;
        }
      });
    }
    
    // Apply coupon discount if exists
    let totalDiscount = 0;
    if (orderData.coupon && orderData.coupon.discount_value) {
      const discountValue = parseFloat(orderData.coupon.discount_value);
      if (orderData.coupon.discount_type === 'percentage') {
        totalDiscount = (subtotal * discountValue) / 100;
      } else {
        totalDiscount = discountValue;
      }
    }
    
    const discountedSubtotal = Math.max(0, subtotal - totalDiscount);
    const shippingCost = parseFloat(orderData.delivery_price) || 0;
    
    return discountedSubtotal + shippingCost;
  };

  // Handle input changes for editing
  const handleInputChange = (field, value) => {
    setEditedOrder(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle product quantity changes
  const handleProductQuantityChange = (sku, newQuantity) => {
    if (newQuantity < 1) return;
    
    setEditedProducts(prev => 
      prev.map(product => 
        product.sku === sku 
          ? { ...product, qty: newQuantity.toString() }
          : product
      )
    );
  };

  // Save changes
  const handleSave = async () => {
    try {
      // Using the same API structure as regular orders but with rpo_bulk endpoint
      // Convert products array back to comma-separated sku and qty strings (old format)
      const skuList = editedProducts.map(product => product.sku);
      const qtyList = editedProducts.map(product => product.qty);
      const skuString = skuList.join(',');
      const qtyString = qtyList.join(',');

      const response = await axios.post(`https://gokulnair.com/rpo_bulk/api/order/update/${editedOrder.order_id}`, {
        order_id: editedOrder.order_id,
        name: editedOrder.name,
        email: editedOrder.email,
        phone: editedOrder.phone,
        shipping_address: editedOrder.shipping_address,
        shipping_city: editedOrder.shipping_city,
        shipping_state: editedOrder.shipping_state,
        shipping_pincode: editedOrder.shipping_pincode,
        delivery_type: editedOrder.delivery_type,
        delivery_price: editedOrder.delivery_price,
        payment_type: editedOrder.payment_type,
        payment_status: editedOrder.payment_status,
        sku: skuString,
        qty: qtyString
      });
      
      if (response.data.message && response.data.message.includes('successfully')) {
        setOrder(editedOrder);
        setProducts(editedProducts);
        setEditing(false);
        setSuccessMessage(response.data.message);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 5000);
      } else {
        setShowErrorMessage(true);
        setTimeout(() => setShowErrorMessage(false), 5000);
      }
    } catch (err) {
      console.error('Error saving changes:', err);
      setShowErrorMessage(true);
      setTimeout(() => setShowErrorMessage(false), 5000);
    }
  };

  // Cancel editing
  const handleCancel = () => {
    setEditedOrder(order);
    setEditedProducts(products);
    setEditing(false);
    setShowProductSelector(false);
  };

  // Add product to order
  const handleAddProduct = (product) => {
    // Check if product is already in the order
    const isAlreadyAdded = editedProducts.some(p => p.sku === product.sku);
    if (!isAlreadyAdded) {
      // Add product with default quantity 1
      const productWithQty = { ...product, qty: "1" };
      setEditedProducts(prev => [...prev, productWithQty]);
    }
  };

  // Remove product from order
  const handleRemoveProduct = (sku) => {
    setEditedProducts(prev => prev.filter(p => p.sku !== sku));
  };

  // Check if product is already in order
  const isProductInOrder = (sku) => {
    return editedProducts.some(p => p.sku === sku);
  };

  // Filter products for search
  const filteredProducts = allProducts.filter(product =>
    product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading order details...</div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="error-message">{error}</div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="error-message">Order not found</div>
      </Layout>
    );
  }

  // Define currentOrder and currentProducts for editing mode
  const currentOrder = editing && editedOrder ? editedOrder : order;
  const currentProducts = editing && editedProducts ? editedProducts : products;
  
  // Ensure we have valid data before proceeding
  if (!currentOrder) {
    return (
      <Layout>
        <div className="order-details-content">
          <div className="error-message">Order data not available</div>
          <button onClick={() => navigate('/dashboard')} className="back-button">
            Back to Dashboard
          </button>
        </div>
      </Layout>
    );
  }
  
  const productInfos = getProductInfo(currentOrder.sku);
  const total = calculateTotal(currentOrder);

  return (
    <Layout>
      <div className="order-details-content">
        {/* Success Message */}
        {showSuccessMessage && (
          <div className="success-message">
            <div className="success-icon">✅</div>
            <div className="success-content">
              <h3>{successMessage}</h3>
              <p>Order ID: {currentOrder.order_id}</p>
              <p className="success-details">Order has been updated and saved to the system.</p>
            </div>
            <button 
              className="close-message-btn"
              onClick={() => setShowSuccessMessage(false)}
            >
              ×
            </button>
          </div>
        )}

        {/* Error Message */}
        {showErrorMessage && (
          <div className="error-message-box">
            <div className="error-icon">❌</div>
            <div className="error-content">
              <h3>Update Failed</h3>
              <p>Failed to update order. Please try again.</p>
            </div>
            <button 
              className="close-message-btn"
              onClick={() => setShowErrorMessage(false)}
            >
              ×
            </button>
          </div>
        )}

        <header className="order-details-header">
          <div className="header-left">
            <h1>Bulk Order Details</h1>
            <p>Order ID: {currentOrder.order_id}</p>
          </div>
          <div className="header-actions">
            {editing ? (
              <>
                <button onClick={handleSave} className="save-button">
                  Save Changes
                </button>
                <button onClick={handleCancel} className="cancel-button">
                  Cancel
                </button>
              </>
            ) : (
              currentOrder.payment_status === 2 ? (
                <button className="edit-button-disabled" disabled>
                  Edit Order (Confirmed by Admin)
                </button>
              ) : (
                <button onClick={() => setEditing(true)} className="edit-button">
                  Edit Order
                </button>
              )
            )}
            <button onClick={() => navigate('/dashboard')} className="back-button">
              Back to Dashboard
            </button>
          </div>
        </header>

        <main className="order-details-main">
          <div className="order-card">
            <div className="order-section">
              <h2>Customer Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>Name:</label>
                  {editing ? (
                    <input
                      type="text"
                      value={currentOrder.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span>{currentOrder.name}</span>
                  )}
                </div>
                <div className="info-item">
                  <label>Email:</label>
                  {editing ? (
                    <input
                      type="email"
                      value={currentOrder.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span>{currentOrder.email}</span>
                  )}
                </div>
                <div className="info-item">
                  <label>Phone:</label>
                  {editing ? (
                    <input
                      type="tel"
                      value={currentOrder.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span>{currentOrder.phone}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="order-section">
              <h2>Shipping Address</h2>
              <div className="address-info">
                {editing ? (
                  <div className="address-edit">
                    <input
                      type="text"
                      value={currentOrder.shipping_address}
                      onChange={(e) => handleInputChange('shipping_address', e.target.value)}
                      className="edit-input"
                      placeholder="Shipping Address"
                    />
                    <div className="address-row">
                      <input
                        type="text"
                        value={currentOrder.shipping_city}
                        onChange={(e) => handleInputChange('shipping_city', e.target.value)}
                        className="edit-input"
                        placeholder="City"
                      />
                      <input
                        type="text"
                        value={currentOrder.shipping_state}
                        onChange={(e) => handleInputChange('shipping_state', e.target.value)}
                        className="edit-input"
                        placeholder="State"
                      />
                      <input
                        type="text"
                        value={currentOrder.shipping_pincode}
                        onChange={(e) => handleInputChange('shipping_pincode', e.target.value)}
                        className="edit-input"
                        placeholder="Pincode"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <p>{currentOrder.shipping_address}</p>
                    <p>{currentOrder.shipping_city}, {currentOrder.shipping_state} {currentOrder.shipping_pincode}</p>
                  </>
                )}
              </div>
            </div>

            <div className="order-section">
              <h2>Order Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>Order Date:</label>
                  <span>{new Date(currentOrder.created_at).toLocaleDateString()}</span>
                </div>
                <div className="info-item">
                  <label>Delivery Type:</label>
                  {editing ? (
                    <select
                      value={currentOrder.delivery_type}
                      onChange={(e) => handleInputChange('delivery_type', e.target.value)}
                      className="edit-select"
                    >
                      <option value="standard">Standard</option>
                      <option value="express">Express</option>
                      <option value="overnight">Overnight</option>
                    </select>
                  ) : (
                    <span>{currentOrder.delivery_type}</span>
                  )}
                </div>
                <div className="info-item">
                  <label>Payment Type:</label>
                  {editing ? (
                    <select
                      value={currentOrder.payment_type}
                      onChange={(e) => handleInputChange('payment_type', parseInt(e.target.value))}
                      className="edit-select"
                    >
                      <option value={0}>Zelle</option>
                      <option value={1}>Cash App</option>
                      <option value={2}>Stripe</option>
                    </select>
                  ) : (
                    <span>
                      <span className={`payment-type-badge ${getPaymentTypeText(currentOrder.payment_type).toLowerCase().replace(' ', '-')}`}>
                        {getPaymentTypeText(currentOrder.payment_type)}
                      </span>
                    </span>
                  )}
                </div>
                <div className="info-item">
                  <label>Payment Status:</label>
                  {editing ? (
                    <select
                      value={currentOrder.payment_status}
                      onChange={(e) => handleInputChange('payment_status', parseInt(e.target.value))}
                      className="edit-select"
                    >
                      <option value={0}>Pending</option>
                      <option value={1}>Confirmed by User</option>
                      <option value={2}>Confirmed by Admin</option>
                      <option value={3}>Rejected by Admin</option>
                      <option value={4}>Rejected by User</option>
                    </select>
                  ) : (
                    <span>
                      <span className={`status-badge ${getPaymentStatusClass(currentOrder.payment_status)}`}>
                        {getPaymentStatusText(currentOrder.payment_status)}
                      </span>
                    </span>
                  )}
                </div>
                <div className="info-item">
                  <label>Coupon Code:</label>
                  {currentOrder.coupon ? (
                    <div className="coupon-info">
                      <span className="coupon-code">{currentOrder.coupon.code}</span>
                      <span className="coupon-discount">
                        {currentOrder.coupon.discount_type === 'percentage' 
                          ? `${currentOrder.coupon.discount_value}% off`
                          : `$${currentOrder.coupon.discount_value} off`
                        }
                      </span>
                    </div>
                  ) : (
                    <span>No coupon applied</span>
                  )}
                </div>
              </div>
            </div>

            <div className="order-section">
              <div className="section-header">
                <h2>Order Items ({currentProducts.length})</h2>
                {editing && (
                  <button 
                    onClick={() => setShowProductSelector(!showProductSelector)}
                    className="add-product-button"
                  >
                    {showProductSelector ? 'Hide Product Selector' : 'Add Products'}
                  </button>
                )}
              </div>

              {editing && showProductSelector && (
                <div className="product-selector">
                  <div className="search-container">
                    <input
                      type="text"
                      placeholder="Search products by name or SKU..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>
                  <div className="products-grid">
                    {filteredProducts.map((product) => (
                      <div 
                        key={product.id} 
                        className={`product-selector-item ${isProductInOrder(product.sku) ? 'selected' : ''}`}
                        onClick={() => handleAddProduct(product)}
                      >
                        <div className="product-selector-image">
                          <img 
                            src={product.image_src} 
                            alt={product.title}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <div className="image-fallback" style={{ display: 'none' }}>
                            {product.title}
                          </div>
                        </div>
                        <div className="product-selector-details">
                          <h4>{product.title}</h4>
                          <p className="product-sku">SKU: {product.sku}</p>
                          <p className="product-price">${product.price}</p>
                          {isProductInOrder(product.sku) && (
                            <span className="added-badge">✓ Added</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="products-list-simple">
                {productInfos && productInfos.length > 0 ? (
                  productInfos.map((product, index) => {
                    let quantity = 0;
                    if (product.qty) {
                      quantity = parseInt(product.qty) || 0;
                    } else {
                      const qtyList = currentOrder.qty.split(',').map(q => q.trim());
                      quantity = parseInt(qtyList[index]) || 0;
                    }
                    
                    const subtotal = parseFloat(product.price) * quantity;
                    
                    return (
                      <div key={product.id} className="product-item-simple">
                        <div className="product-info">
                          <span className="product-title">{product.title}</span>
                          <span className="product-sku">SKU: {product.sku}</span>
                          <span className="product-price">${parseFloat(product.price).toFixed(2)}</span>
                          <span className="product-quantity">
                            Qty: {editing ? (
                              <input
                                type="number"
                                min="1"
                                value={quantity}
                                onChange={(e) => handleProductQuantityChange(product.sku, parseInt(e.target.value))}
                                className="quantity-input"
                              />
                            ) : (
                              quantity
                            )}
                          </span>
                          <span className="product-total">Total: ${subtotal.toFixed(2)}</span>
                        </div>
                        {editing && (
                          <button 
                            onClick={() => handleRemoveProduct(product.sku)}
                            className="remove-product-button-simple"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="no-products">
                    <p>No products found for this order.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="order-section">
              <h2>Order Summary</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>Subtotal:</label>
                  <span>${(total - parseFloat(currentOrder.delivery_price || 0)).toFixed(2)}</span>
                </div>
                {currentOrder.coupon && currentOrder.coupon.discount_value && (
                  <div className="info-item">
                    <label>Coupon Discount:</label>
                    <span className="coupon-discount-amount">
                      -${(total - parseFloat(currentOrder.delivery_price || 0) - (calculateTotalWithCoupon(currentOrder) - parseFloat(currentOrder.delivery_price || 0))).toFixed(2)}
                    </span>
                  </div>
                )}
                {currentOrder.discounted_price && parseFloat(currentOrder.discounted_price) > 0 && (
                  <>
                    <div className="info-item">
                      <label>Original Total:</label>
                      <span className="original-price">${total.toFixed(2)}</span>
                    </div>
                    <div className="info-item">
                      <label>Discounted Price:</label>
                      <span className="discounted-price">${parseFloat(currentOrder.discounted_price).toFixed(2)}</span>
                    </div>
                    <div className="info-item savings-row">
                      <label>Total Savings:</label>
                      <span className="savings-value">${(total - parseFloat(currentOrder.discounted_price)).toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className="info-item">
                  <label>Shipping:</label>
                  <span>${parseFloat(currentOrder.delivery_price || 0).toFixed(2)}</span>
                </div>
                <div className="info-item total-row">
                  <label>Final Total:</label>
                  <span className="total-value">
                    ${currentOrder.discounted_price && parseFloat(currentOrder.discounted_price) > 0 
                      ? (parseFloat(currentOrder.discounted_price) + parseFloat(currentOrder.delivery_price || 0)).toFixed(2)
                      : calculateTotalWithCoupon(currentOrder).toFixed(2)
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </Layout>
  );
};

export default BulkOrderDetails; 